import React from 'react';

import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import Container from '@hero/ui-kit/layout/Container';
import { MembershipLogGroup, PreparedMembershipLog } from './usePreparedMembershipLogs';
import * as Style from './style.module.scss';

const gridTemplateColumns = '2.4rem 2fr 2fr 1fr';

const uniqueInOrder = (values: string[]): string[] =>
    values.filter((value, index) => values.indexOf(value) === index);

const LogRow: React.FC<{ item: PreparedMembershipLog }> = ({ item }) => (
    <Container gridTemplateColumns="2fr 2fr 1fr" gridColumnGap="regular">
        <strong>{item.title}</strong>

        <div>{item.description}</div>

        <div className="align-right">{item.timestamp}</div>
    </Container>
);

const MembershipLogLineItem: React.FC<{ group: MembershipLogGroup }> = ({ group }) => {
    const [expanded, setExpanded] = React.useState(false);

    if (group.repeatCount < 2) {
        const [item] = group.items;

        return (
            <Container
                gridTemplateColumns={gridTemplateColumns}
                gridColumnGap="regular"
                className={Style.logLineItem}
            >
                <div className={Style.caretIcon} />

                <strong>{item.title}</strong>

                <div>{item.description}</div>

                <div className="align-right">{item.timestamp}</div>
            </Container>
        );
    }

    const unit = group.items.slice(0, group.unitLength);
    const headerTitle = uniqueInOrder(unit.map((item) => item.title)).join(' → ');
    const headerDescription = uniqueInOrder(
        unit.map((item) => item.description ?? '').filter(Boolean)
    ).join(', ');

    // Items are sorted newest-first, so the range reads oldest – newest.
    const newest = group.items[0].timestamp;
    const oldest = group.items[group.items.length - 1].timestamp;

    return (
        <div>
            <ClickableOpacity
                alt={expanded ? 'Collapse occurrences' : 'Expand occurrences'}
                onClick={() => setExpanded(!expanded)}
                styles={{ width: '100%', textAlign: 'left', lineHeight: 'inherit' }}
            >
                <Container
                    gridTemplateColumns={gridTemplateColumns}
                    gridColumnGap="regular"
                    className={Style.logLineItem}
                >
                    <div className={Style.caretIcon}>
                        <CaretIcon size="small" direction={expanded ? 'down' : 'forward'} />
                    </div>

                    <strong>
                        {headerTitle}{' '}
                        <span className={Style.repeatCount}>×{group.repeatCount}</span>
                    </strong>

                    <div>{headerDescription}</div>

                    <div className="align-right">
                        {oldest === newest ? (
                            newest
                        ) : (
                            <>
                                {oldest}
                                <br />- {newest}
                            </>
                        )}
                    </div>
                </Container>
            </ClickableOpacity>

            {expanded && (
                <div className={Style.membershipOccurrences}>
                    {group.items.map((item, index) => (
                        <LogRow key={`${item.title}-${item.timestamp}-${index}`} item={item} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MembershipLogLineItem;
